// SDK ⇄ app boundary for native satisfaction forms.
//
// The backend/SDK is **form**-named; the app is **survey**-named. Every alias between the two
// vocabularies lives here so the stores/components never touch a `Form*` symbol directly. Grep the
// SDK docs by the `Form*` names; grep the app by the `Survey*`/`ISurvey*` names.
//
// Known deltas handled here (SDK v1 — see backend handoff):
// - MULTIPLE_CHOICE: SDK value is `multiple_choice_unscored`; the app enum value is `multiple_choice`.
// - `required`, `order` and `minLabel`/`maxLabel` are all in the question DTOs (create + update) and on
//   the read model → sent + read here. FLAG: re-verify server-side persistence of `required`/`order` live
//   (they were dropped in earlier betas).
// - `allowMultiple` (single- vs multi-select for MULTIPLE_CHOICE) is NOT in the contract (no field on the
//   create/update DTO nor the read model) → not persisted; every MC question round-trips as multi-select.
//   FLAG: the builder's "sélection multiple autorisée" toggle is inert until the backend adds the field.
// - Content activités (Texte/Document/Vidéo/Code embed) are NOT in the contract → never round-trip.
import type {
  CreateFormQuestionDto,
  CreateFormTemplateDto,
  CreateSessionFormDto,
  FormQuestionAnswerDto,
  FormRespondPayload,
  FormTemplateDetailResponse,
  FormTemplateQuestionResponse,
  FormTemplateResponse,
  SessionFormRecipientRow,
  SessionFormsTrackingResponse,
  SessionFormSummary,
  SessionFormSummaryOffset,
  SubmitFormAnswerDto,
  UpdateFormQuestionDto,
} from "@simbelapp/order-sdk";
import {
  CreateFormQuestionDtoSubTypeEnum,
  CreateSessionFormDtoOffsetAnchorEnum,
  CreateSessionFormDtoOffsetDirectionEnum,
  CreateSessionFormDtoOffsetUnitEnum,
  CreateSessionFormDtoSendModeEnum,
  FormRespondPayloadTypeEnum,
  SessionFormSummaryOffsetAnchorEnum,
  SessionFormSummaryOffsetDirectionEnum,
  SessionFormSummaryOffsetUnitEnum,
  SessionFormSummaryStatusEnum,
} from "@simbelapp/order-sdk";
import { SurveyActivityTypeEnum, SurveyTypeEnum } from "~/utils/enums/satisfaction-surveys.enums";
import {
  SessionSurveyOffsetAnchorEnum,
  SessionSurveyOffsetDirectionEnum,
  SessionSurveyOffsetUnitEnum,
  SessionSurveyParticipantStatusEnum,
  SessionSurveyRecipientEnum,
  SessionSurveySendModeEnum,
  SessionSurveyStatusEnum,
} from "~/utils/enums/session-surveys.enums";
import type {
  ISurveyActivity,
  ISurveyAnswer,
  ISurveyTemplate,
  ISurveyTemplateDraft,
} from "~/utils/interfaces/satisfaction-surveys-interfaces";
import type {
  ICreateSessionSurveyPayload,
  ISessionSurvey,
  ISessionSurveyAnswer,
  ISessionSurveyModele,
  ISessionSurveyParticipant,
  ISessionSurveyRecap,
  ISessionSurveySchedule,
  ISurveyAnswerValue,
} from "~/utils/interfaces/session-surveys-interfaces";

// --- template type (hot/cold) ------------------------------------------------
export const SURVEY_TYPE_FROM_SDK: Record<string, SurveyTypeEnum> = {
  [FormRespondPayloadTypeEnum.Direct]: SurveyTypeEnum.DIRECT,
  [FormRespondPayloadTypeEnum.Delayed]: SurveyTypeEnum.DELAYED,
};

export const SURVEY_TYPE_TO_SDK: Record<SurveyTypeEnum, "direct" | "delayed"> = {
  [SurveyTypeEnum.DIRECT]: FormRespondPayloadTypeEnum.Direct,
  [SurveyTypeEnum.DELAYED]: FormRespondPayloadTypeEnum.Delayed,
};

// --- question sub-type -------------------------------------------------------
// The contract only carries the 3 satisfaction question types (no content blocks).
export const QUESTION_TYPE_FROM_SDK: Record<string, SurveyActivityTypeEnum> = {
  [CreateFormQuestionDtoSubTypeEnum.Rating]: SurveyActivityTypeEnum.RATING,
  [CreateFormQuestionDtoSubTypeEnum.OpenQuestion]: SurveyActivityTypeEnum.OPEN,
  [CreateFormQuestionDtoSubTypeEnum.MultipleChoiceUnscored]: SurveyActivityTypeEnum.MULTIPLE_CHOICE,
  [CreateFormQuestionDtoSubTypeEnum.MultipleChoiceQuestion]: SurveyActivityTypeEnum.MULTIPLE_CHOICE_SCORED,
  [CreateFormQuestionDtoSubTypeEnum.AssociationQuestion]: SurveyActivityTypeEnum.ASSOCIATION,
  [CreateFormQuestionDtoSubTypeEnum.GapFillingQuestion]: SurveyActivityTypeEnum.GAP_FILLING,
};

export const QUESTION_TYPE_TO_SDK: Partial<Record<SurveyActivityTypeEnum, CreateFormQuestionDtoSubTypeEnum>> = {
  [SurveyActivityTypeEnum.RATING]: CreateFormQuestionDtoSubTypeEnum.Rating,
  [SurveyActivityTypeEnum.OPEN]: CreateFormQuestionDtoSubTypeEnum.OpenQuestion,
  [SurveyActivityTypeEnum.MULTIPLE_CHOICE]: CreateFormQuestionDtoSubTypeEnum.MultipleChoiceUnscored,
  [SurveyActivityTypeEnum.MULTIPLE_CHOICE_SCORED]: CreateFormQuestionDtoSubTypeEnum.MultipleChoiceQuestion,
  [SurveyActivityTypeEnum.ASSOCIATION]: CreateFormQuestionDtoSubTypeEnum.AssociationQuestion,
  [SurveyActivityTypeEnum.GAP_FILLING]: CreateFormQuestionDtoSubTypeEnum.GapFillingQuestion,
};

/**
 * SDK answer rows → the builder's answer list, per question type.
 *
 * The backend stores every type in the same `quiz_answers` table (buildQuizAnswerRows), so the read
 * shape is uniform (`{id, label, order, question, isRightAnswer}`) and it's the TYPE that says how to
 * read it:
 *  - un-scored MC → just the labels
 *  - scored MC    → labels + which are right
 *  - association  → `question` is the left prompt, `label` the right match
 *  - texte à trou → ONE ROW PER ACCEPTED SPELLING, all sharing the gap's `order`. Regroup by order,
 *    or a 2-gap question with 3 accepted answers each reads back as 6 gaps.
 */
function mapAnswerOptions(
  type: SurveyActivityTypeEnum | null,
  options?: FormTemplateQuestionResponse["answers"],
): ISurveyAnswer[] | undefined {
  if (!options?.length) return undefined;

  if (type === SurveyActivityTypeEnum.GAP_FILLING) {
    const byGap = new Map<number, ISurveyAnswer>();
    for (const option of options) {
      const gap = byGap.get(option.order);
      if (gap) {
        gap.gapAnswers?.push(option.label);
        continue;
      }
      byGap.set(option.order, { id: option.id, label: "", order: option.order, gapAnswers: [option.label] });
    }
    return [...byGap.values()].sort((a, b) => a.order - b.order);
  }

  return options.map((option) => ({
    id: option.id,
    label: option.label,
    order: option.order,
    ...(type === SurveyActivityTypeEnum.MULTIPLE_CHOICE_SCORED ? { isRightAnswer: !!option.isRightAnswer } : {}),
    ...(type === SurveyActivityTypeEnum.ASSOCIATION ? { question: option.question ?? "" } : {}),
  }));
}

/** SDK form question → app activité. `required`/`order`/`minLabel`/`maxLabel` all round-trip now. */
export function mapFormQuestion(question: FormTemplateQuestionResponse): ISurveyActivity {
  const type = QUESTION_TYPE_FROM_SDK[question.type] ?? null;
  return {
    id: question.id,
    type,
    order: question.order,
    title: question.title,
    description: question.description ?? undefined,
    required: question.required,
    minLabel: question.minLabel ?? undefined,
    maxLabel: question.maxLabel ?? undefined,
    isSystem: question.isSystem,
    answers: mapAnswerOptions(type, question.answers),
    // Respondent-only halves — present on the public capture payload, absent when authoring.
    pairPrompts: question.pairPrompts?.map((prompt) => ({ id: prompt.id, label: prompt.label, order: prompt.order })),
    pairCandidates: question.pairCandidates ?? undefined,
    gaps: question.gaps?.map((gap) => gap.order),
  };
}

/** Public-capture payload → the questions the wizard renders (ordered) + display name/type. */
export function mapRespondPayload(payload: FormRespondPayload): {
  questions: ISurveyActivity[];
  formName: string;
  type: SurveyTypeEnum;
} {
  return {
    questions: [...payload.questions].sort((a, b) => a.order - b.order).map(mapFormQuestion),
    formName: payload.formName,
    type: SURVEY_TYPE_FROM_SDK[payload.type] ?? SurveyTypeEnum.DIRECT,
  };
}

/**
 * App answer → SDK submit answer. Each question type fills exactly ONE field of the DTO:
 * - RATING → `value` (the note as a string) · OPEN → `value` (free text)
 * - MULTIPLE_CHOICE, scored or not → `selectedOptions` (option ids; scored MC is graded on these)
 * - ASSOCIATION → `pairs` [{leftId, chosen}] · GAP_FILLING → `gapAnswers` [{order, value}]
 * The last two arrive here as maps (see ISurveyAnswerValue) and are flattened to the arrays the API wants.
 */
export function toSubmitAnswerDto(answer: ISessionSurveyAnswer): SubmitFormAnswerDto {
  const asMap = (value: ISurveyAnswerValue): Record<string, string> =>
    value && typeof value === "object" && !Array.isArray(value) ? value : {};

  switch (answer.type) {
    case SurveyActivityTypeEnum.MULTIPLE_CHOICE:
    case SurveyActivityTypeEnum.MULTIPLE_CHOICE_SCORED:
      return { questionId: answer.questionId, selectedOptions: Array.isArray(answer.value) ? answer.value : [] };
    case SurveyActivityTypeEnum.ASSOCIATION:
      return {
        questionId: answer.questionId,
        pairs: Object.entries(asMap(answer.value)).map(([leftId, chosen]) => ({ leftId, chosen })),
      };
    case SurveyActivityTypeEnum.GAP_FILLING:
      return {
        questionId: answer.questionId,
        gapAnswers: Object.entries(asMap(answer.value)).map(([order, value]) => ({ order: Number(order), value })),
      };
    default:
      return { questionId: answer.questionId, value: answer.value == null ? null : String(answer.value) };
  }
}

// --- session tracking (Évaluations tab) --------------------------------------
// Offset enums share the SDK's string values; explicit maps keep it typesafe without casts.
const OFFSET_UNIT_TO_SDK = {
  hour: CreateSessionFormDtoOffsetUnitEnum.Hour,
  day: CreateSessionFormDtoOffsetUnitEnum.Day,
} as const;
const OFFSET_DIRECTION_TO_SDK = {
  before: CreateSessionFormDtoOffsetDirectionEnum.Before,
  after: CreateSessionFormDtoOffsetDirectionEnum.After,
} as const;
const OFFSET_ANCHOR_TO_SDK = {
  session_start: CreateSessionFormDtoOffsetAnchorEnum.Start,
  session_end: CreateSessionFormDtoOffsetAnchorEnum.End,
} as const;

// Reverse maps (SDK summary → app) — used to prefill the "Modifier" modal from getSessionFormsTracking.
const OFFSET_UNIT_FROM_SDK: Record<SessionFormSummaryOffsetUnitEnum, SessionSurveyOffsetUnitEnum> = {
  [SessionFormSummaryOffsetUnitEnum.Hour]: SessionSurveyOffsetUnitEnum.HOUR,
  [SessionFormSummaryOffsetUnitEnum.Day]: SessionSurveyOffsetUnitEnum.DAY,
};
const OFFSET_DIRECTION_FROM_SDK: Record<SessionFormSummaryOffsetDirectionEnum, SessionSurveyOffsetDirectionEnum> = {
  [SessionFormSummaryOffsetDirectionEnum.Before]: SessionSurveyOffsetDirectionEnum.BEFORE,
  [SessionFormSummaryOffsetDirectionEnum.After]: SessionSurveyOffsetDirectionEnum.AFTER,
};
const OFFSET_ANCHOR_FROM_SDK: Record<SessionFormSummaryOffsetAnchorEnum, SessionSurveyOffsetAnchorEnum> = {
  [SessionFormSummaryOffsetAnchorEnum.Start]: SessionSurveyOffsetAnchorEnum.SESSION_START,
  [SessionFormSummaryOffsetAnchorEnum.End]: SessionSurveyOffsetAnchorEnum.SESSION_END,
};
// The app tab has only Programmée / Envoyée; a form-level `completed` (all responded) still reads Envoyée.
const SURVEY_STATUS_FROM_SDK: Record<SessionFormSummaryStatusEnum, SessionSurveyStatusEnum> = {
  [SessionFormSummaryStatusEnum.Scheduled]: SessionSurveyStatusEnum.SCHEDULED,
  [SessionFormSummaryStatusEnum.Sent]: SessionSurveyStatusEnum.SENT,
  [SessionFormSummaryStatusEnum.Completed]: SessionSurveyStatusEnum.SENT,
};

function mapOffsetToSchedule(offset?: SessionFormSummaryOffset | null): ISessionSurveySchedule | null {
  if (!offset) return null;
  return {
    amount: offset.n,
    unit: OFFSET_UNIT_FROM_SDK[offset.unit],
    direction: OFFSET_DIRECTION_FROM_SDK[offset.direction],
    anchor: OFFSET_ANCHOR_FROM_SDK[offset.anchor],
  };
}

const PARTICIPANT_STATUS_FROM_SDK: Record<string, SessionSurveyParticipantStatusEnum> = {
  scheduled: SessionSurveyParticipantStatusEnum.SCHEDULED,
  sent: SessionSurveyParticipantStatusEnum.SENT,
  completed: SessionSurveyParticipantStatusEnum.COMPLETED,
};

function mapRecipientRow(row: SessionFormRecipientRow): ISessionSurveyParticipant {
  return {
    id: row.id,
    token: row.id, // the recipient id IS the personalized-link token
    name: row.name,
    // `name` is the addressee — the MANAGER for an à-froid enquête — which is why the table reads
    // attendeeName/managerName instead (see ISessionSurveyParticipant).
    attendeeName: row.attendeeName,
    managerName: row.managerName ?? null,
    status: PARTICIPANT_STATUS_FROM_SDK[row.status] ?? SessionSurveyParticipantStatusEnum.SENT,
    sentAt: row.sentAt ?? null,
    respondedAt: row.respondedAt ?? null,
    globalScore: row.globalScore ?? null,
    // Per-question answers aren't in the tracking payload; the response panel drills into
    // getEvaluation(recipientId) for them (session-surveys.store → fetchResponseDetail).
    answers: [],
  };
}

function mapSessionFormSummary(summary: SessionFormSummary): ISessionSurvey {
  const type = SURVEY_TYPE_FROM_SDK[summary.type] ?? SurveyTypeEnum.DIRECT;
  // Survey-level status/schedule/templateId now come straight from the summary (beta.5) → the "Modifier"
  // modal can prefill the modèle + schedule. Per-question snapshot still isn't in the tracking payload.
  return {
    id: summary.sessionFormId,
    type,
    templateId: summary.templateId,
    templateName: summary.templateName,
    status: SURVEY_STATUS_FROM_SDK[summary.status] ?? SessionSurveyStatusEnum.SENT,
    sentAt: null,
    schedule: mapOffsetToSchedule(summary.offset),
    recipients:
      type === SurveyTypeEnum.DIRECT ? SessionSurveyRecipientEnum.PARTICIPANTS : SessionSurveyRecipientEnum.MANAGERS,
    questions: [],
    participants: summary.recipients.map(mapRecipientRow),
  };
}

/** Session forms tracking → the recap KPIs + the hot/cold surveys the tab renders. */
export function mapSessionFormsTracking(response: SessionFormsTrackingResponse): {
  recap: ISessionSurveyRecap;
  surveys: ISessionSurvey[];
} {
  // Response rates come back as 0–1; the tab renders them with a "%" suffix, so scale to 0–100.
  const toPct = (rate: number | null): number | null => (rate == null ? null : Math.round(rate * 100));
  return {
    recap: {
      avgHot: response.kpis.avgHot,
      avgCold: response.kpis.avgCold,
      responseRateHot: toPct(response.kpis.responseRateHot),
      responseRateCold: toPct(response.kpis.responseRateCold),
    },
    surveys: response.forms.map(mapSessionFormSummary),
  };
}

/** Active templates of one type → the "Nouvelle enquête" modèle dropdown (questions not needed to create). */
export function mapTemplateOption(template: FormTemplateResponse): ISessionSurveyModele {
  return {
    id: template.formTemplateId,
    name: template.name,
    type: SURVEY_TYPE_FROM_SDK[template.type] ?? SurveyTypeEnum.DIRECT,
    questions: [],
  };
}

/** "Nouvelle enquête" payload → createSessionForm DTO (the template's type drives hot/cold audience). */
export function toCreateSessionFormDto(payload: ICreateSessionSurveyPayload): CreateSessionFormDto {
  const scheduled = payload.sendMode === SessionSurveySendModeEnum.SCHEDULED;
  return {
    templateId: payload.templateId,
    sendMode: scheduled ? CreateSessionFormDtoSendModeEnum.Scheduled : CreateSessionFormDtoSendModeEnum.Now,
    offset:
      scheduled && payload.schedule
        ? {
            n: payload.schedule.amount,
            unit: OFFSET_UNIT_TO_SDK[payload.schedule.unit],
            direction: OFFSET_DIRECTION_TO_SDK[payload.schedule.direction],
            anchor: OFFSET_ANCHOR_TO_SDK[payload.schedule.anchor],
          }
        : null,
  };
}

// --- template authoring (Settings builder) -----------------------------------
/** Template list row → the Settings table projection (name + type). */
export function mapTemplateListItem(template: FormTemplateResponse): ISurveyTemplate {
  return {
    id: template.formTemplateId,
    name: template.name,
    type: SURVEY_TYPE_FROM_SDK[template.type] ?? SurveyTypeEnum.DIRECT,
  };
}

/** Full template (with questions) → the builder's editable draft. Only question types round-trip. */
export function mapTemplateDetailToDraft(detail: FormTemplateDetailResponse): ISurveyTemplateDraft {
  return {
    id: detail.formTemplateId,
    name: detail.name,
    type: SURVEY_TYPE_FROM_SDK[detail.type] ?? SurveyTypeEnum.DIRECT,
    activities: [...detail.questions].sort((a, b) => a.order - b.order).map(mapFormQuestion),
  };
}

/** Draft header → create DTO (name + immutable type; the system question is auto-added server-side). */
export function toCreateFormTemplateDto(name: string, type: SurveyTypeEnum): CreateFormTemplateDto {
  return { name, type: SURVEY_TYPE_TO_SDK[type] };
}

// MC options are sent as plain label strings; the server owns their ids (returned as SurveyAnswerOption on read).
/**
 * The correction key, in the two shapes the backend's `buildQuizAnswerRows` expects:
 * un-scored MC uses `options` (plain labels, no correction); every graded type uses `answers`.
 * Sending the wrong one silently persists no key at all, so these two must stay in lockstep with
 * form-templates-utils.service.ts.
 */
const questionOptions = (activity: ISurveyActivity): string[] | undefined =>
  activity.type === SurveyActivityTypeEnum.MULTIPLE_CHOICE
    ? (activity.answers ?? []).map((answer) => answer.label)
    : undefined;

const questionAnswers = (activity: ISurveyActivity): FormQuestionAnswerDto[] | undefined => {
  const answers = activity.answers ?? [];
  switch (activity.type) {
    case SurveyActivityTypeEnum.MULTIPLE_CHOICE_SCORED:
      return answers.map((answer, i) => ({ order: i, answer: answer.label, isRightAnswer: !!answer.isRightAnswer }));
    case SurveyActivityTypeEnum.ASSOCIATION:
      // One row per pair: question = left prompt, answer = right match (isRightAnswer forced true server-side).
      return answers.map((answer, i) => ({ order: i, question: answer.question ?? "", answer: answer.label }));
    case SurveyActivityTypeEnum.GAP_FILLING:
      // One row per GAP; the server flattens gapAnswers into a row per accepted spelling.
      return answers.map((answer, i) => ({ order: i, gapAnswers: answer.gapAnswers ?? [] }));
    default:
      return undefined;
  }
};

/** New draft question → add DTO. `required`/`order`/`minLabel`/`maxLabel` all persist. */
export function toCreateFormQuestionDto(activity: ISurveyActivity, order: number): CreateFormQuestionDto {
  return {
    subType:
      QUESTION_TYPE_TO_SDK[activity.type as SurveyActivityTypeEnum] ?? CreateFormQuestionDtoSubTypeEnum.OpenQuestion,
    title: activity.title,
    description: activity.description ?? null,
    required: activity.required ?? null,
    minLabel: activity.minLabel ?? null,
    maxLabel: activity.maxLabel ?? null,
    options: questionOptions(activity) ?? null,
    answers: questionAnswers(activity) ?? null,
    order,
  };
}

/** Edited draft question → patch DTO (type is immutable server-side; `order` now persists reordering). */
export function toUpdateFormQuestionDto(activity: ISurveyActivity, order: number): UpdateFormQuestionDto {
  return {
    title: activity.title,
    description: activity.description ?? null,
    required: activity.required ?? null,
    minLabel: activity.minLabel ?? null,
    maxLabel: activity.maxLabel ?? null,
    options: questionOptions(activity) ?? null,
    answers: questionAnswers(activity) ?? null,
    order,
  };
}
